import {
  Avatar as ChakraAvatar,
  AvatarProps as ChakraAvatarProps,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";

import {
  IcoUnknownCompanyAvatar,
  IcoUnknownUserAvatar,
} from "@/common/components/Icons";
import { API_URL } from "@/common/utils/env";
import { skipImageProcessing } from "@reflag/shared/imageAPI";

export const AvatarSizeMap = {
  "2xs": 5,
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
  xl: 14,
  "2xl": 16,
  full: 20,
};

export type AvatarSizeProp = keyof typeof AvatarSizeMap;

export type AvatarProps = Omit<ChakraAvatarProps, "size"> & {
  size?: AvatarSizeProp;
  type?: "user" | "company";
};

// Custom Avatar component to avoid Chakra Avatar's loading logic and bad re-rendering
export const Avatar = ({
  name,
  src,
  size,
  type = "user",
  ...rest
}: AvatarProps) => {
  const [showImage, setShowImage] = useState(!!src);
  const bgUnknown = useColorModeValue("gray.300", "gray.500");

  useEffect(() => {
    setShowImage(!!src);
  }, [src]);

  const processedSrc = useMemo(() => {
    if (!src) return undefined;
    return !skipImageProcessing.some((domain) => src.startsWith(domain))
      ? `${API_URL}${src}`
      : src;
  }, [src]);

  return !!processedSrc && showImage ? (
    <Image
      alt={name}
      borderRadius="full"
      boxSize={AvatarSizeMap[size ?? "2xs"]}
      objectFit="contain"
      src={processedSrc}
      onError={() => setShowImage(false)}
      {...rest}
    />
  ) : name ? (
    <ChakraAvatar name={name} size={size ?? "2xs"} {...rest} />
  ) : type === "company" ? (
    <IcoUnknownCompanyAvatar
      boxSize={AvatarSizeMap[size ?? "2xs"]}
      color={bgUnknown}
    />
  ) : (
    <IcoUnknownUserAvatar
      boxSize={AvatarSizeMap[size ?? "2xs"]}
      color={bgUnknown}
    />
  );
};
